import React, { useEffect, useMemo, useState } from "react";
import { Button, Loader } from "@mantine/core";
import moment from "moment";
import { DoorState } from "../interfaces/doors";
import {
  DOOR_FRESHNESS_TICK_MS,
  DoorLiveStatus,
  deriveDoorLiveStatus,
  doorReadingTimes,
  floorAggregate,
} from "./doorOccupancyModel";

type DoorOccupancyProps = {
  doors: DoorState[];
  isWebsocketOpen: boolean;
  isLoading: boolean;
  isError: boolean;
  error?: string;
  onRetry: () => void;
  onOpenTrafficGraph?: () => void;
};

const liveStatusPresentation: Record<
  DoorLiveStatus,
  { label: string; dotClassName: string; textClassName: string; title: string }
> = {
  live: {
    label: "Live",
    dotClassName: "bg-success",
    textClassName: "text-grayText3",
    title: "WebSocket connected and every assigned counter is reporting.",
  },
  updating: {
    label: "Updating",
    dotClassName: "bg-action",
    textClassName: "text-action",
    title: "The live connection is reconnecting; recent readings remain visible.",
  },
  delayed: {
    label: "Delayed",
    dotClassName: "bg-alert",
    textClassName: "text-alert",
    title: "One or more counter readings are missing or more than 15 minutes old.",
  },
};

function latestDoorReading(door: DoorState): moment.Moment | null {
  const readings = [
    door.eventTime,
    door.current?.latestPeriodAt,
    door.current?.lastEventAt,
  ]
    .filter((timestamp): timestamp is string => Boolean(timestamp))
    .map((timestamp) => moment(timestamp))
    .filter((timestamp) => timestamp.isValid())
    .sort((left, right) => right.valueOf() - left.valueOf());

  return readings[0] || null;
}

function updatedLabel(door: DoorState): string {
  const latestReading = latestDoorReading(door);
  return latestReading
    ? `Counter reading ${latestReading.fromNow()}`
    : "Waiting for the first counter reading";
}

function counterLabel(count: number): string {
  return `${count} ${count === 1 ? "counter" : "counters"}`;
}

// BE counters can drift negative across the daily reset (exits without
// matching entries); an occupancy estimate below zero is meaningless, so
// the display floors it at 0.
function displayNet(net: number | undefined): number {
  return Math.max(0, net ?? 0);
}

export default function DoorOccupancy({
  doors,
  isWebsocketOpen,
  isLoading,
  isError,
  error,
  onRetry,
  onOpenTrafficGraph,
}: DoorOccupancyProps) {
  const [now, setNow] = useState(() => Date.now());
  const aggregate = useMemo(() => floorAggregate(doors), [doors]);
  const readingTimes = useMemo(
    () => doorReadingTimes(doors, aggregate.current),
    [aggregate, doors],
  );
  const liveStatus = deriveDoorLiveStatus(
    isWebsocketOpen,
    readingTimes,
    now,
  );
  const statusPresentation = liveStatusPresentation[liveStatus];
  const showLoading = isLoading && doors.length === 0;
  const showError = isError && doors.length === 0;
  const baseline = aggregate.current.baselineAt
    ? moment(aggregate.current.baselineAt)
    : null;
  const baselineLabel = baseline?.isValid()
    ? `Baseline ${aggregate.current.baseline} at ${baseline.format("HH:mm")}`
    : "Waiting for today's baseline";

  useEffect(() => {
    if (doors.length === 0) {
      return;
    }

    const interval = setInterval(
      () => setNow(Date.now()),
      DOOR_FRESHNESS_TICK_MS,
    );
    return () => clearInterval(interval);
  }, [doors.length]);

  return (
    <section
      aria-labelledby="door-occupancy-title"
      className="w-full mb-8 rounded-[20px] border border-solid border-grayText4 bg-white px-5 py-5 sm:px-6"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div>
          <h2
            id="door-occupancy-title"
            className="text-xl font-md text-grayText7"
          >
            Floor door traffic
          </h2>
          <p className="text-sm text-grayText5 mt-1">
            Daily entries and exits from people counters currently assigned to
            this floor
          </p>
        </div>
        <div className="flex items-center gap-3">
          {onOpenTrafficGraph && (
            <Button
              variant="subtle"
              color="#FB6514"
              size="compact-sm"
              onClick={onOpenTrafficGraph}
            >
              View traffic graph
            </Button>
          )}
          {!showLoading && !showError && doors.length > 0 && (
            <div
              role="status"
              title={statusPresentation.title}
              className={`flex items-center gap-2 text-sm ${statusPresentation.textClassName}`}
            >
              <span
                aria-hidden="true"
                className={`h-2.5 w-2.5 rounded-full ${statusPresentation.dotClassName}`}
              />
              {statusPresentation.label}
            </div>
          )}
        </div>
      </div>

      {isError && doors.length > 0 && (
        <div
          role="alert"
          className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-actionLight px-4 py-3"
        >
          <p className="text-sm text-grayText3">
            Background reconciliation failed. Showing the last known counter
            readings; live connection status is shown above.
          </p>
          <Button
            variant="subtle"
            color="#FB6514"
            size="compact-sm"
            onClick={onRetry}
          >
            Refresh now
          </Button>
        </div>
      )}

      {showLoading ? (
        <div className="flex items-center gap-3 py-5 text-grayText3">
          <Loader size="sm" color="#FB6514" />
          Loading door traffic...
        </div>
      ) : showError ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-alertLight px-4 py-3">
          <p className="text-sm text-grayText3">
            {error || "Door traffic is temporarily unavailable."}
          </p>
          <Button
            variant="subtle"
            color="#FB6514"
            size="compact-sm"
            onClick={onRetry}
          >
            Try again
          </Button>
        </div>
      ) : doors.length === 0 ? (
        <p className="rounded-xl bg-lightGray px-4 py-4 text-sm text-grayText3">
          No people counter is assigned to this floor.
        </p>
      ) : (
        <div aria-live="polite" className="space-y-4">
          <article className="rounded-2xl bg-actionLight px-4 py-4 sm:px-5">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="max-w-2xl">
                <p className="text-sm font-md text-grayText7">
                  Estimated floor daily net
                </p>
                <p className="mt-1 text-sm text-grayText3">
                  Entries minus exits since the daily zero baseline. This is a
                  traffic estimate, not the number of people currently present.
                  A moved counter contributes only from its current floor
                  assignment.
                </p>
                <p className="mt-2 text-xs text-grayText5">
                  {baselineLabel} ·{" "}
                  {counterLabel(aggregate.current.sensorCount)} currently
                  assigned and included
                </p>
              </div>
              <p
                aria-label={`Estimated floor daily net ${displayNet(
                  aggregate.current.net,
                )}`}
                className="text-4xl font-md text-action"
              >
                {displayNet(aggregate.current.net)}
              </p>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 border-0 border-t border-solid border-[#F5D8C3] pt-3">
              <div>
                <p className="text-xs text-grayText5">Floor entries today</p>
                <p className="text-lg font-md text-grayText7">
                  {aggregate.current.totalIn}
                </p>
              </div>
              <div>
                <p className="text-xs text-grayText5">Floor exits today</p>
                <p className="text-lg font-md text-grayText7">
                  {aggregate.current.totalOut}
                </p>
              </div>
            </div>
          </article>

          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            {doors.map((door) => (
              <article
                key={door.id || door.deviceSN}
                className="rounded-2xl border border-solid border-grayText4 px-4 py-4"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-md text-grayText7">
                      {door.alias || door.deviceSN}
                    </h3>
                    <p className="text-xs text-grayText5 mt-1">
                      {updatedLabel(door)}
                    </p>
                  </div>
                  <div className="rounded-xl bg-lightGray px-4 py-2 text-right">
                    <p className="text-2xl font-md text-grayText7">
                      {displayNet(door.current?.net)}
                    </p>
                    <p className="text-xs text-grayText3">daily net</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 mt-4 border-0 border-t border-solid border-grayText4 pt-3">
                  <div>
                    <p className="text-xs text-grayText5">Entered today</p>
                    <p className="text-lg font-md text-grayText7">
                      {door.current?.totalIn ?? 0}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-grayText5">Left today</p>
                    <p className="text-lg font-md text-grayText7">
                      {door.current?.totalOut ?? 0}
                    </p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
